import { createAction } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { contentManagementActions } from 'apps/cms/store'
import { IDefaultReplyTrigger } from 'apps/defaultReply/defaultReplyInterfaces'
import defaultReplyAdapter from 'apps/defaultReply/models/defaultReplyAdapter'
import { alert } from 'common/core'
import { ChannelType } from 'common/core/constants/ChannelType'
import { DefaultReply, DefaultReplyType } from 'shared/api/common/schemas/flow/defaultReply'
import { defaultReplyApi } from 'shared/api/requests/defaultReply'
import { createAsyncAction } from 'shared/lib/redux'
import { accountNotificationsListener } from 'utils/services/notificationsService'
import { INotificationServiceData } from 'utils/services/notificationsService/notificationsServiceTypes'

export const loadDefaultReply = createAsyncAction(
  'defaultReply/loadDefaultReply',
  async (channel: ChannelType = ChannelType.FB) => {
    const { data } = await defaultReplyApi.getData({ query: { channel } })

    return data
  },
)

export const updateTriggerDefaultReplyEnabled = createAsyncAction(
  'defaultReply/updateTriggerDefaultReplyEnabled',
  async ({ channel = ChannelType.FB, enabled }: { channel?: ChannelType; enabled: boolean }) => {
    const { data } = await defaultReplyApi.switch({
      query: { channel, default_enabled: Number(enabled) },
    })

    if (data.enabled) {
      alert(l.translate('Default reply activated'), 'success')
    } else {
      alert(l.translate('Default reply deactivated'))
    }

    return data
  },
)

export const changeDefaultReplyType = createAsyncAction(
  'defaultReply/changeDefaultReplyType',
  async ({ channel = ChannelType.FB, type }: { channel?: ChannelType; type: DefaultReplyType }) => {
    const { data } = await defaultReplyApi.changeType({ body: { channel, type } })
    return data
  },
)

export const setDefaultReplyFlow = createAsyncAction(
  'defaultReply/setDefaultReplyFlow',
  async ({ flowId, channel = ChannelType.FB }: { flowId?: string; channel?: ChannelType }) => {
    const { data } = await defaultReplyApi.replaceFlow({ body: { flow_ns: flowId, channel } })
    return data
  },
)

export const createDefaultReplyFlow = createAsyncAction(
  'defaultReply/createDefaultReplyFlow',
  async (channel: ChannelType = ChannelType.FB, { dispatch }) => {
    const flowName = `${channel[0].toUpperCase()}${channel.slice(1)} Default Reply`

    const result = await dispatch(
      contentManagementActions.createAutomation({
        name: flowName,
      }),
    ).unwrap()

    return await dispatch(
      setDefaultReplyFlow({
        flowId: result?.flow?.ns,
        channel,
      }),
    ).unwrap()
  },
)

export const changeInstagramDefaultReplySources = createAsyncAction(
  'defaultReply/changeInstagramDefaultReplySources',
  async ({
    channel = ChannelType.INSTAGRAM,
    isStoryOn,
  }: {
    channel?: ChannelType
    isStoryOn: boolean
  }) => {
    const { data } = await defaultReplyApi.changeSource({
      body: {
        channel,
        sources: { direct: true, story: isStoryOn },
      },
    })
    return data
  },
)

export const removeDefaultReplyFlow = createAsyncAction(
  'defaultReply/removeDefaultReplyFlow',
  async (channel: ChannelType) => {
    const { data } = await defaultReplyApi.removeFlow({ body: { channel } })
    return data
  },
)

export const updateDefaultReplyByChannel = createAction(
  'defaultReply/updateDefaultReplyByChannel',
  (channel: ChannelType, data: DefaultReply) => ({
    payload: {
      channel,
      data,
    },
  }),
)

export const defaultReplyUpdatedNotification = createAction<IDefaultReplyTrigger>(
  'defaultReply/defaultReplyUpdatedNotification',
)

accountNotificationsListener.on(
  'default_reply_updated',
  (data: INotificationServiceData<DefaultReply>, dispatch) => {
    dispatch(updateDefaultReplyByChannel(data.model.channel, data.model))
    dispatch(defaultReplyUpdatedNotification(defaultReplyAdapter(data.model)))
  },
)
